import { Form, Row } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import React, { useContext, useEffect, useState } from 'react';
import { IAmlz, Ic1 } from 'types/amlz';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';
import PhoneNumberInput from 'components/molecules/PhoneNumberInput';

import checkLeiApi from 'services/akcionar/emise/checkLei';
import { IRozcestnikData } from 'services/akcionar/emise/rozcestnikISIN';
import { IcisStaty } from 'services/cisStaty';
import vyjadreniCdcp from 'services/spravaEmitenta/vyjadreniCdcp';

import { EZadostStatus } from 'enums/amlz/zadostStatus';
import { EAnoNe } from 'enums/anoNe';

import anoNe from 'data/codebooks/anoNeSelect';

import { cisloUctuValidation, kodBankyValidation, predcisliValidation } from 'utils/validations/bankUcetValidation';
import dicValidation from 'utils/validations/dicValidation';
import emailValidation from 'utils/validations/emailValidation';
import leiValidation from 'utils/validations/leiValidation';
import pscValidation from 'utils/validations/pscValidation';

interface IC1 {
    isLegalEntity: boolean;
    C1data?: Ic1;
    rozcestnikData?: IRozcestnikData['spolecnost'];
    countries: IcisStaty[];
    nextStep: () => void;
    SubmitFunc: <T extends keyof IAmlz['amlz']>(key: T, values: IAmlz['amlz'][T]) => void;
    setLeiError: React.Dispatch<React.SetStateAction<boolean>>;
    setSaving: React.Dispatch<React.SetStateAction<boolean>>;
    isC6edit: string;
    leiError: boolean;
}

const C1: React.FC<IC1> = ({
    C1data,
    rozcestnikData,
    countries,
    nextStep,
    SubmitFunc,
    isC6edit,
    leiError,
    setLeiError,
    setSaving,
    isLegalEntity
}) => {
    const { amlz, aktivniFirma } = useContext(Context);
    const [form] = Form.useForm();
    const [modalMessage, setModalMessage] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [invalidLei, setInvalidLei] = useState<string>('');
    const [, setRerender] = useState<boolean>(false);
    const setForceRerender = useState<boolean>(false)[1];

    const rerender = () => {
        setForceRerender(val => !val);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (fieldsValue: any) => {
        if (fieldsValue.lei)
            checkLeiApi(fieldsValue.lei)
                .then(data => {
                    if (
                        (fieldsValue.kost === 'CZ' && data.ic !== fieldsValue.idmb) ||
                        (fieldsValue.kost !== 'CZ' && fieldsValue.kost !== data.stat)
                    ) {
                        setInvalidLei('FORMULARE_LEI_VEDENO_PRO_JINOU_SPOL');
                    } else if (data.stav !== 'ISSUED' && data.stav !== 'PENDING_TRANSFER') {
                        setInvalidLei(data.stav === 'LAPSED' ? 'FORMULARE_LEI_LAPSED_FO' : 'FORMULARE_LEI_ZRUSENE');
                    } else {
                        const values = Object.fromEntries(
                            Object.entries({
                                ...C1data,
                                ...fieldsValue,
                                ...(fieldsValue.madic && {
                                    madic: fieldsValue.madic === EAnoNe.ANO
                                })
                            }).filter(([, v]) => v !== undefined && v !== '')
                        );
                        SubmitFunc('c1', values);
                        nextStep();
                    }
                })
                .catch(() => {
                    setInvalidLei('FORMULARE_AMLZ_ROZCESTNIK_INVALID_LEI');
                })
                .finally(() => {
                    setLoading(false);
                });
        else {
            const values = Object.fromEntries(
                Object.entries({
                    ...C1data,
                    ...fieldsValue,
                    ...(fieldsValue.madic && {
                        madic: fieldsValue.madic === EAnoNe.ANO
                    })
                }).filter(([, v]) => v !== undefined && v !== '')
            );
            SubmitFunc('c1', values);
            nextStep();
        }
    };

    useEffect(() => {
        setSaving(true);
        // C1data?.idmb && C1data?.lei && setTimeout(() => SubmitFunc('c1', C1data), 500);
        if (amlz && amlz.sprava) setModalMessage(amlz.sprava);
        if (amlz && amlz.stav === EZadostStatus.KDOPLNENI) {
            // zobrazi vyjadrenie cdcp v modal okne
            setLoading(true);
            vyjadreniCdcp(amlz.idzadosti ? amlz.idzadosti : 0)
                .then(data => setModalMessage(data.vyjadreni))
                .finally(() => setLoading(false));
        }
        if (amlz?.amlz?.c1?.lei) {
            setLoading(true);
            checkLeiApi(amlz.amlz?.c1?.lei)
                .then(
                    response =>
                        (response.stav === 'RETIRED' ||
                            response.stav === 'LAPSED' ||
                            response.stav === 'ANNULLED' ||
                            response.stav === 'CANCELLED') &&
                        setLeiError(true)
                )
                .finally(() => setLoading(false));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        form.setFieldsValue({
            ...C1data,
            ...(C1data?.madic && {
                madic: typeof C1data?.madic === 'boolean' ? (C1data.madic ? EAnoNe.ANO : EAnoNe.NE) : undefined
            })
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [C1data]);

    /* useEffect(() => {
        saving && onFinish(form.getFieldsValue());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saving]); */

    return (
        <>
            {C1data && (
                <LoadingWrapper loading={loading}>
                    <FormWrapper onFinish={onFinish} name="C1" outForm={form}>
                        <InputComponent
                            disabled={!!C1data?.idmb}
                            label="IDENTIFIKACNI_CISLO"
                            name="idmb"
                            initialValue={C1data?.idmb}
                            type="text"
                            maxLength={10}
                        />
                        <InputComponent
                            disabled={!!C1data?.nazev}
                            label={isLegalEntity ? 'ISIN_IDENTIFIKACE_FIRMA_LEGALENTITY' : 'ISIN_IDENTIFIKACE_FIRMA'}
                            name="nazev"
                            initialValue={C1data?.nazev}
                            type="text"
                            maxLength={500}
                        />
                        <InputComponent
                            disabled={!!C1data?.lei}
                            label="LEI_CELE"
                            name="lei"
                            initialValue={C1data?.lei || rozcestnikData?.lei}
                            maxLength={20}
                            type="text"
                            notRequired={isLegalEntity}
                            extramessage={leiError ? 'CHECK_LEI_MATKASLEI_NEPLATNE_LEI' : ''}
                            rules={leiValidation()}
                        />
                        <SelectComponent
                            name="stat"
                            isFormItem={true}
                            labelParagraph={
                                isLegalEntity ? 'FORMULARE_PROVOZOVNA_STATY_LEGALENTITY' : 'FORMULARE_PROVOZOVNA_STATY'
                            }
                            mode="multiple"
                            defaultValue={C1data?.stat}
                            data={countries ? countries : []}
                            onChange={val => {
                                setRerender(pv => !pv);
                                !val.includes(aktivniFirma?.stat) &&
                                    form.setFieldsValue({
                                        stat: [aktivniFirma?.stat, ...val]
                                    });
                            }}
                        />
                        <InputComponent
                            label="ISIN_IDENTIFIKACE_PRAVNI_FORMA"
                            name="pravniforma"
                            initialValue={C1data?.pravniforma}
                            maxLength={500}
                            type="text"
                        />
                        {isLegalEntity && (
                            <>
                                <SelectComponent
                                    name="madic"
                                    labelParagraph="ISIN_IDENTIFIKACE_PLATCE"
                                    isFormItem={true}
                                    defaultValue={
                                        typeof C1data?.madic === 'boolean'
                                            ? C1data.madic
                                                ? EAnoNe.ANO
                                                : EAnoNe.NE
                                            : undefined
                                    }
                                    data={anoNe}
                                    onChange={() => {
                                        form.resetFields(['dic']);
                                        rerender();
                                    }}
                                />
                                {form.getFieldValue('madic') === EAnoNe.ANO && (
                                    <InputComponent
                                        //    initialValue={isin?.dic || identifikacePutData?.dic}
                                        label="ISIN_IDENTIFIKACE_DIC"
                                        name="dic"
                                        maxLength={14}
                                        initialValue={C1data?.dic}
                                        rules={dicValidation()}
                                    />
                                )}
                                <InputComponent
                                    label="EMAIL"
                                    name="email"
                                    initialValue={C1data?.email}
                                    type="text"
                                    maxLength={100}
                                    rules={emailValidation()}
                                />
                                <PhoneNumberInput
                                    label="TELEFON"
                                    name="telefon"
                                    initialValue={C1data?.telefon}
                                    maxLength={10}
                                />
                            </>
                        )}
                        <Title
                            level={5}
                            dividerTitle={true}
                            text={
                                isLegalEntity
                                    ? 'ISIN_IDENTIFIKACE_ADRESA_LEGALENTITY'
                                    : 'ISIN_IDENTIFIKACE_ADRESA_SIDLA'
                            }
                        />
                        <InputComponent
                            label="ULICE_CP"
                            name="adma"
                            initialValue={C1data?.adma}
                            type="text"
                            maxLength={50}
                        />
                        <InputComponent
                            label="MESTO"
                            name="mema"
                            initialValue={C1data?.mema}
                            type="text"
                            maxLength={50}
                        />
                        <InputComponent
                            label="PSC"
                            name="pscm"
                            initialValue={C1data?.pscm}
                            maxLength={10}
                            type="text"
                            rules={pscValidation('kost')}
                        />
                        <SelectComponent
                            isFormItem={true}
                            name="kost"
                            labelParagraph="STAT_SIDLA"
                            defaultValue={C1data?.kost ? C1data.kost : ''}
                            data={countries ? countries : []}
                        />
                        {isLegalEntity && (
                            <>
                                <Title
                                    level={5}
                                    dividerTitle={true}
                                    text="UCET_LEGALENTITY"
                                    dividerTooltip="UCET_LEGALENTITY_TOOLTIP"
                                />
                                <InputComponent
                                    label="PREDCISLI_UCTU"
                                    name="predcisli"
                                    initialValue={C1data?.predcisli ? C1data.predcisli : ''}
                                    notRequired={true}
                                    maxLength={6}
                                    rules={predcisliValidation()}
                                />
                                <InputComponent
                                    label="CISLO_UCTU"
                                    name="cislouctu"
                                    initialValue={C1data?.cislouctu ? C1data.cislouctu : ''}
                                    maxLength={10}
                                    rules={cisloUctuValidation()}
                                />
                                <InputComponent
                                    label="CISLO_BANKY"
                                    name="banka"
                                    initialValue={C1data?.banka ? C1data.banka : ''}
                                    maxLength={4}
                                    rules={kodBankyValidation()}
                                />
                            </>
                        )}
                        <Modal
                            title={<TranslateText>{'VYJADRENI_CDCP'}</TranslateText>}
                            open={!!modalMessage}
                            onCancel={() => setModalMessage('')}
                            afterClose={() => {
                                setModalMessage('');
                            }}
                            footer={
                                <Row className="button-row-modal">
                                    <ButtonComponent
                                        id="C1ZavritBtn"
                                        buttontext="ZAVRIT"
                                        onClick={() => {
                                            setModalMessage('');
                                        }}
                                    />
                                </Row>
                            }
                        >
                            <Row>
                                <Paragraph text={modalMessage} />
                            </Row>
                        </Modal>

                        <Row className="button-row">
                            {isC6edit ? (
                                <ButtonComponent
                                    type="primary"
                                    onClick={() => form.submit()}
                                    id="C1PrehledBtn"
                                    buttontext="POKRACOVAT_NA_ZHRNUTI"
                                />
                            ) : (
                                <ButtonComponent
                                    type="primary"
                                    onClick={() => {
                                        setSaving(false);
                                        setTimeout(() => form.submit(), 100);
                                    }}
                                    disabled={leiError}
                                    id="C1PokracovatBtn"
                                    buttontext="POKRACOVAT"
                                />
                            )}
                        </Row>
                    </FormWrapper>
                </LoadingWrapper>
            )}
            <Modal
                title={<TranslateText>NEVALIDNI_LEI</TranslateText>}
                open={!!invalidLei}
                onCancel={() => setInvalidLei('')}
                afterClose={() => {
                    setInvalidLei('');
                    setLoading(false);
                }}
                footer={
                    <Row className="button-row-modal">
                        <ButtonComponent
                            id="C0ZpetModalBtn"
                            buttontext="ZAVRIT"
                            type="default"
                            onClick={() => {
                                setInvalidLei('');
                                setLoading(false);
                            }}
                        />
                    </Row>
                }
            >
                <Row>
                    <Paragraph text={invalidLei} />
                </Row>
            </Modal>
        </>
    );
};

export default C1;
